import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { PendingInviteWithId } from "@/hooks/useFirestore";
import { UserPermission } from "@/types/user";
import { Edit, MoreHorizontal, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import EditTeamMemberForm from "./EditTeamMemberForm";

interface TeamListProps {
  users: UserPermission[];
  pendingInvites: PendingInviteWithId[];
  loading: boolean;
  currentUserId?: string;
  onRemoveMember: (user: UserPermission) => void;
  onEditMember: (user: UserPermission) => void;
  onCancelInvite: (invite: PendingInviteWithId) => void;
  canManageTeam: boolean;
}

const TeamList: React.FC<TeamListProps> = ({
  users,
  pendingInvites,
  loading,
  currentUserId,
  onRemoveMember,
  onEditMember,
  onCancelInvite,
  canManageTeam,
}) => {
  const [userToEdit, setUserToEdit] = useState<UserPermission | null>(null);
  const [userEditSession, setUserEditSession] = useState(0);
  const renderedUserToEdit = useRetainedValue(userToEdit);

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin":
        return "default";
      case "editor":
        return "outline";
      case "collector":
        return "secondary";
      case "viewer":
        return "outline";
      default:
        return "outline";
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case "admin":
        return "Admin";
      case "editor":
        return "Editor";
      case "collector":
        return "Collector";
      case "viewer":
        return "Viewer";
      default:
        return role;
    }
  };

  // Find the current user's role
  const currentUserRole = users.find((u) => u.id === currentUserId)?.role;

  const handleEditSubmit = (data: Omit<UserPermission, "id">) => {
    if (renderedUserToEdit) {
      // Merge the updated form data with the user's id to create the updated user
      onEditMember({ ...renderedUserToEdit, ...data });
      setUserToEdit(null);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            {canManageTeam && <TableHead className="w-[100px]">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                {user.firstName} {user.lastName}
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Badge variant={getRoleBadgeVariant(user.role)}>{getRoleDisplayName(user.role)}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant={user.status === "active" ? "default" : "secondary"}>
                  {user.status === "active" ? "Active" : "Disabled"}
                </Badge>
              </TableCell>
              {canManageTeam && (
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => {
                        setUserEditSession((current) => current + 1);
                        setUserToEdit(user);
                      }}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      {user.id !== currentUserId && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => onRemoveMember(user)}
                            className="text-destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          ))}
          {/* Pending Invites */}
          {pendingInvites.map((invite) => (
            <TableRow
              key={`pending-${invite.id}`}
              className="bg-muted/30">
              <TableCell className="text-muted-foreground">
                {invite.firstName} {invite.lastName}
              </TableCell>
              <TableCell className="text-muted-foreground">{invite.email}</TableCell>
              <TableCell>
                <Badge variant={getRoleBadgeVariant(invite.role)}>{getRoleDisplayName(invite.role)}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant="outline">Pending</Badge>
              </TableCell>
              {canManageTeam && (
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => onCancelInvite(invite)}>
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={!!userToEdit}
        onOpenChange={() => setUserToEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          {renderedUserToEdit && (
            <EditTeamMemberForm
              key={`${renderedUserToEdit.id}-${userEditSession}`}
              member={renderedUserToEdit}
              onSubmit={handleEditSubmit}
              onCancel={() => setUserToEdit(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeamList;
